import dayjs from 'dayjs';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import Box from '../../../uikit/Box';
import Table from '../../../uikit/Table';
import ChangeValue from '../../common/ChangeValue';
import { useMobxInstance } from '../../../clientUtils/mobxUtil';
import { apiClient } from '../../../clientUtils/apiClient';
import { StrategyWeightResponse } from '../../../../server/response/responseScheme';

class ViewStore {
    latestWeight: StrategyWeightResponse | null = null;
    isLoading = true;
    holdings: {
        ticker: string;
        name: string; // TODO: 종목명 조회 필요
        weight: number;
        stockReturnTotal: number;
        stockReturnDaily: number;
        stockReturnMonthly: number;
        stockReturnYearly: number;
    }[] = [];

    constructor() {
        makeAutoObservable(this);
    }

    async fetch(strategyHash: string) {
        this.isLoading = true;

        try {
            const latestWeight = await apiClient.strategy.public.getLatestWeight.$query({
                hash: strategyHash,
            });

            runInAction(() => {
                this.latestWeight = latestWeight;
                this.isLoading = false;
                this.holdings = this.calculateHoldings();
            });
        } catch (error) {
            console.error('Failed to fetch latest weight:', error);
            runInAction(() => {
                this.isLoading = false;
                this.holdings = [];
            });
        }
    }

    private calculateHoldings() {
        if (!this.latestWeight || !this.latestWeight.weightsJson) {
            return [];
        }

        const holdings = Object.entries(this.latestWeight.weightsJson).map(([ticker, weight]) => ({
            ticker,
            name: ticker, // TODO: 종목명 조회 필요
            weight: weight as number,
            stockReturnTotal: 0, // TODO: 종목별 수익률 계산 필요
            stockReturnDaily: 0,
            stockReturnMonthly: 0,
            stockReturnYearly: 0,
        }));

        // 비중이 높은 순으로 정렬
        holdings.sort((a, b) => b.weight - a.weight);

        return holdings;
    }
}

interface Props {
    strategyHash: string;
}

const StrategyStockReturnsBox = observer(({ strategyHash }: Props) => {
    const viewStore = useMobxInstance(ViewStore);

    useEffect(() => {
        viewStore.fetch(strategyHash);
    }, [viewStore, strategyHash]);

    if (viewStore.isLoading) {
        return <Box isLoading={true} />;
    }

    return (
        <Box>
            <Box.Title title="종목별 수익률" />
            <Table fixed>
                <Table.Header>종목</Table.Header>
                <Table.Header>누적</Table.Header>
                <Table.Header>어제</Table.Header>
                <Table.Header>{dayjs().format('M월')}</Table.Header>
                <Table.Header>{dayjs().format('YYYY년')}</Table.Header>

                {viewStore.holdings.map((holding) => (
                    <Table.Row key={holding.ticker}>
                        <Table.Cell>
                            <span className="font-medium">{holding.ticker}</span>
                        </Table.Cell>
                        <Table.Cell>
                            <span className="tabular-nums">
                                <ChangeValue
                                    value={holding.stockReturnTotal * 100}
                                    suffix="%"
                                    sign={true}
                                    round={1}
                                />
                            </span>
                        </Table.Cell>
                        <Table.Cell>
                            <span className="tabular-nums">
                                <ChangeValue
                                    value={holding.stockReturnDaily * 100}
                                    suffix="%"
                                    sign={true}
                                    round={1}
                                />
                            </span>
                        </Table.Cell>
                        <Table.Cell>
                            <span className="tabular-nums">
                                <ChangeValue
                                    value={holding.stockReturnMonthly * 100}
                                    suffix="%"
                                    sign={true}
                                    round={1}
                                />
                            </span>
                        </Table.Cell>
                        <Table.Cell>
                            <span className="tabular-nums">
                                <ChangeValue
                                    value={holding.stockReturnYearly * 100}
                                    suffix="%"
                                    sign={true}
                                    round={1}
                                />
                            </span>
                        </Table.Cell>
                    </Table.Row>
                ))}
            </Table>
        </Box>
    );
});

export default StrategyStockReturnsBox;
